import React, { Ref, forwardRef } from "react"
import { Form, FormikProps } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import * as Yup from "yup"
import { CompanyFormValues } from "../companyTypes"
import { CompanyCreateUpdateContent } from "./CompanyCreateUpdateContent"
import { AgreementOptions, BusinessType, EntityHistory, InvoiceType } from "../../supportEntitiesTypes"

type CreateOrEditCompanyFormProps = Readonly<{
    initialValues?: CompanyFormValues | null
    history?: EntityHistory
    onSubmit: (formData: CompanyFormValues) => void
}>

const defaultInitialValues: CompanyFormValues = {
    mainEanNumber: "",
    urlIdentifier: "",
    businessType: "",
    city: "",
    name: "",
    businessNumber: "",
    contactPerson: "",
    email: "",
    isSystemOwner: "",
    isOfficeHotel: false,
    postalCode: "",
    telephone: "",
    isActive: true,
    isSelfSignUp: true,
    countryCode: "",
    address1: "",
    address2: "",
    eanNumber: "",
    invoiceType: "",
    attention: "",
    debitorNo: "",
    invoiceEmail: "",
    reminderEmail: "",
    poNumber: "",
    url: "",
}

type FormikRef = Ref<FormikProps<CompanyFormValues>>

export const CompanyCreateUpdateForm = forwardRef(
    ({ initialValues, onSubmit, history }: CreateOrEditCompanyFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const companySchema = Yup.object().shape({
            isOfficeHotel: Yup.boolean(),
            isSelfSignUp: Yup.boolean(),
            isActive: Yup.boolean(),
            businessType: Yup.string()
                .oneOf(
                    Object.values(BusinessType),
                    l10n.getString("support-create-entity-error-label-company-invalid-type"),
                )
                .required()
                .label(l10n.getString("support-create-entity-error-label-company-type")),
            mainEanNumber: Yup.string()
                .required()
                .label(l10n.getString("support-create-company-error-label-main-ean-number")),
            urlIdentifier: Yup.string()
                .label(l10n.getString("support-create-company-error-label-url-identifier"))
                .when("isSelfSignUp", {
                    is: (val: AgreementOptions) => Boolean(val),
                    then: (schema) => schema.required().url(),
                    otherwise: (schema) => schema.optional(),
                }),
            isSystemOwner: Yup.string()
                .required()
                .label(l10n.getString("support-create-entity-error-label-system-owner")),
            businessNumber: Yup.string()
                .required()
                .label(l10n.getString("support-create-entity-error-label-company-number")),
            name: Yup.string().required().label(l10n.getString("support-create-entity-error-label-company-name")),
            countryCode: Yup.string().required().label(l10n.getString("support-create-entity-error-label-country")),
            address1: Yup.string().required().label(l10n.getString("support-create-entity-error-label-address")),
            address2: Yup.string().label(l10n.getString("support-create-entity-error-label-address")),
            postalCode: Yup.string().required().label(l10n.getString("support-create-entity-error-label-postal-code")),
            city: Yup.string().required().label(l10n.getString("support-create-entity-error-label-city")),
            contactPerson: Yup.string()
                .required()
                .label(l10n.getString("support-create-entity-error-label-contact-person")),
            email: Yup.string().email().required().label(l10n.getString("support-create-entity-error-label-email")),
            telephone: Yup.string().required().label(l10n.getString("support-create-entity-error-label-telephone")),
            invoiceType: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-invoice-type"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            eanNumber: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-ean-number"))
                .when("invoiceType", {
                    is: (val: InvoiceType) => val === InvoiceType.EAN_NUMBER,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            invoiceEmail: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-invoice-email"))
                .when("invoiceType", {
                    is: (val: InvoiceType) => val === InvoiceType.EMAIL,
                    then: (schema) => schema.required().email(),
                    otherwise: (schema) => schema.optional(),
                }),
            attention: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-attention"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            debitorNo: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-debitot-no"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            reminderEmail: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-reminder-email"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required().email(),
                    otherwise: (schema) => schema.optional(),
                }),
            poNumber: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-po-number"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
        })

        function handleSubmit(values: CompanyFormValues) {
            onSubmit(values)
        }

        return (
            <Formik<CompanyFormValues>
                enableReinitialize
                initialValues={initialValues ? initialValues : defaultInitialValues}
                validationSchema={companySchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <CompanyCreateUpdateContent selfSignupUrl={initialValues?.url} history={history} />
                </Form>
            </Formik>
        )
    },
)
